import { createTheme, lighten, type Theme, type ThemeOptions } from "@mui/material/styles";
import { deepmerge } from "@mui/utils";
import type {} from "@mui/x-date-pickers/themeAugmentation";

import { adjustColor, hexToRgb } from "@/utils/colors";

import { THEME_PRESETS, type ThemeName } from "./presets";
import type { ThemeOverrides } from "./types";

const baseThemeConfig: ThemeOptions = {
  shape: {
    borderRadius: 4,
  },
  breakpoints: {
    values: { xs: 0, sm: 732, md: 900, lg: 1200, xl: 1536 },
  },
  typography: {
    fontFamily: '"Campton", -apple-system, BlinkMacSystemFont, sans-serif',
    caption: {
      fontSize: "14px",
    },
    body1: {
      fontSize: "16px",
      fontWeight: 400,
    },
    body2: {
      fontSize: "14px",
      fontWeight: 400,
    },
    h2: {
      fontSize: "22px",
      fontWeight: 600,
      lineHeight: 1,
    },
    h3: {
      fontSize: "18px",
      fontWeight: 600,
      lineHeight: 1,
    },
    h4: {
      fontSize: "16px",
      fontWeight: 600,
      lineHeight: 1,
    },
    h6: {
      fontSize: "16px",
      fontWeight: 600,
      lineHeight: 1,
    },
    button: {
      fontSize: "16px",
      fontWeight: 600,
      lineHeight: 1,
    },
  },
  shadows: [
    "none", // 0
    "0px 1px 2px 0px rgba(0, 0, 0, 0.06)", // 1
    "0px 1px 3px 0px rgba(0, 0, 0, 0.06), 0px 0px 1px 0px rgba(0, 0, 0, 0.11)", // 2
    "0px 1px 4px -1px rgba(0, 0, 0, 0.06), 0px 1px 2px 0px rgba(0, 0, 0, 0.11)", // 3
    "0px 2px 5px -1px rgba(0, 0, 0, 0.06), 0px 1px 4px -1px rgba(0, 0, 0, 0.11)", // 4
    "0px 2px 6px -1px rgba(0, 0, 0, 0.06), 0px 1px 4px -1px rgba(0, 0, 0, 0.11)", // 5
    "0px 2px 8px -2px rgba(0, 0, 0, 0.06), 0px 2px 6px -2px rgba(0, 0, 0, 0.11)", // 6
    "0px 3px 10px -2px rgba(0, 0, 0, 0.06), 0px 2px 6px -2px rgba(0, 0, 0, 0.11)", // 7
    "0px 4px 12px -3px rgba(0, 0, 0, 0.06), 0px 2px 8px -2px rgba(0, 0, 0, 0.11)", // 8
    "0px 4px 14px -3px rgba(0, 0, 0, 0.06), 0px 2px 8px -2px rgba(0, 0, 0, 0.11)", // 9
    "0px 5px 16px -3px rgba(0, 0, 0, 0.06), 0px 5px 15px -2px rgba(0, 0, 0, 0.11)", // 10
    "0px 5px 18px -3px rgba(0, 0, 0, 0.06), 0px 3px 10px -2px rgba(0, 0, 0, 0.11)", // 11
    "0px 6px 20px -4px rgba(0, 0, 0, 0.06), 0px 4px 12px -3px rgba(0, 0, 0, 0.11)", // 12
    "0px 7px 22px -4px rgba(0, 0, 0, 0.06), 0px 4px 12px -3px rgba(0, 0, 0, 0.11)", // 13
    "0px 7px 24px -4px rgba(0, 0, 0, 0.06), 0px 4px 14px -3px rgba(0, 0, 0, 0.11)", // 14
    "0px 8px 26px -4px rgba(0, 0, 0, 0.06), 0px 4px 14px -3px rgba(0, 0, 0, 0.11)", // 15
    "0px 8px 28px -4px rgba(0, 0, 0, 0.06), 0px 5px 16px -4px rgba(0, 0, 0, 0.11)", // 16
    "0px 9px 30px -4px rgba(0, 0, 0, 0.06), 0px 5px 16px -4px rgba(0, 0, 0, 0.11)", // 17
    "0px 10px 32px -5px rgba(0, 0, 0, 0.06), 0px 5px 18px -4px rgba(0, 0, 0, 0.11)", // 18
    "0px 10px 34px -5px rgba(0, 0, 0, 0.06), 0px 5px 18px -4px rgba(0, 0, 0, 0.11)", // 19
    "0px 11px 36px -5px rgba(0, 0, 0, 0.06), 0px 6px 20px -4px rgba(0, 0, 0, 0.11)", // 20
    "0px 11px 38px -5px rgba(0, 0, 0, 0.06), 0px 6px 20px -4px rgba(0, 0, 0, 0.11)", // 21
    "0px 12px 40px -5px rgba(0, 0, 0, 0.06), 0px 7px 22px -5px rgba(0, 0, 0, 0.11)", // 22
    "0px 13px 42px -5px rgba(0, 0, 0, 0.06), 0px 7px 22px -5px rgba(0, 0, 0, 0.11)", // 23
    "0px 13px 44px -5px rgba(0, 0, 0, 0.06), 0px 7px 24px -5px rgba(0, 0, 0, 0.11)", // 24
  ],
  components: {
    MuiLink: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.text.accent,
          textDecorationColor: theme.palette.text.accent,
        }),
      },
    },
    MuiButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: Number(theme.shape.borderRadius) * 8,
          textTransform: "none",
          fontWeight: 600,
          padding: "12px 24px",
          fontSize: "16px",
        }),
        sizeLarge: {
          padding: "22px 75px",
        },
        text: ({ theme }) => ({
          color: theme.palette.text.primary,
        }),
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: ({ theme }) => ({
          "& .MuiOutlinedInput-root": {
            borderRadius: Number(theme.shape.borderRadius) * 4,
            backgroundColor: theme.palette.background.paper,
            "& fieldset": {
              borderWidth: "0px",
              top: 0,
            },
            "& fieldset legend": {
              display: "none",
            },
            "&:hover fieldset": {
              borderWidth: "0px",
            },
            "&.Mui-focused fieldset": {
              borderColor: theme.palette.primary.main,
              borderWidth: "2px",
            },
          },
          "& .MuiInputBase-input": {
            padding: "15px",
            "&::placeholder": {
              color: theme.palette.grey[600],
              opacity: 1,
            },
          },
        }),
      },
    },
    MuiFilledInput: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: Number(theme.shape.borderRadius) * 4,
          backgroundColor: theme.palette.grey[200],
          [theme.breakpoints.only("xs")]: {
            backgroundColor: theme.palette.background.paper,
          },
          "&:before, &:after": {
            display: "none",
          },
          "&:hover:not(.Mui-disabled):before": {
            display: "none",
          },
          "&:hover": {
            backgroundColor: theme.palette.grey[200],
            [theme.breakpoints.only("xs")]: {
              backgroundColor: theme.palette.background.paper,
            },
          },
          "&.Mui-error": {
            borderColor: theme.palette.error.main,
            borderWidth: "2px",
            borderStyle: "solid",
          },
          "&.MuiFilledInput-multiline": {
            padding: 0,
          },
        }),
      },
    },
    MuiPickersFilledInput: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: Number(theme.shape.borderRadius) * 4,
          backgroundColor: theme.palette.grey[200],
          [theme.breakpoints.only("xs")]: {
            backgroundColor: theme.palette.background.paper,
          },
          paddingRight: "20px",
          "&:before, &:after": {
            display: "none",
          },
          "&:hover:not(.Mui-disabled):before": {
            display: "none",
          },
          ".MuiPickersSectionList-root": {
            padding: "15px",
          },
          "&:hover": {
            backgroundColor: theme.palette.grey[200],
            [theme.breakpoints.only("xs")]: {
              backgroundColor: theme.palette.background.paper,
            },
          },
          "&.Mui-error": {
            borderColor: theme.palette.error.main,
            borderWidth: "2px",
            borderStyle: "solid",
          },
        }),
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        root: () => ({
          ".MuiInputBase-root": {
            padding: "8px 14px",
          },
        }),
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: ({ theme }) => ({
          transform: "none",
          top: 16,
          left: 16,
          color: theme.palette.text.secondary,
          "&.MuiInputLabel-shrink": {
            display: "none",
          },
          "&.Mui-error": {
            color: theme.palette.text.secondary,
            "& .MuiInputLabel-asterisk": {
              color: theme.palette.text.secondary,
            },
          },
        }),
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          minHeight: "auto",
          marginBottom: 24,
        },
        indicator: ({ theme }) => ({
          height: "2px",
          backgroundColor: theme.palette.primary.main,
        }),
      },
    },
    MuiTab: {
      styleOverrides: {
        root: ({ theme }) => ({
          padding: 4,
          fontWeight: 600,
          textTransform: "none",
          borderBottom: `2px solid ${theme.palette.divider}`,
          "&.Mui-selected": {
            color: theme.palette.text.primary,
            borderColor: theme.palette.primary.main,
          },
        }),
      },
    },
    MuiCard: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: Number(theme.shape.borderRadius) * 4,
          border: `1px solid ${theme.palette.divider}`,
        }),
      },
    },
    MuiPaper: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: Number(theme.shape.borderRadius) * 4,
        }),
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          width: 42,
          height: 26,
          padding: 0,
        },
        switchBase: ({ theme }) => ({
          padding: 0,
          margin: 2,
          transitionDuration: "300ms",

          "&.Mui-checked": {
            transform: "translateX(16px)",
            color: "#fff",
            "& + .MuiSwitch-track": {
              backgroundColor: theme.palette.primary.main,
              opacity: 1,
              border: 0,
              ...theme.applyStyles("dark", {
                backgroundColor: theme.palette.primary.main,
              }),
            },
            "&.Mui-disabled + .MuiSwitch-track": {
              opacity: 0.5,
            },
          },

          "&.Mui-focusVisible .MuiSwitch-thumb": {
            color: theme.palette.primary.main,
            border: "6px solid #fff",
          },

          "&.Mui-disabled .MuiSwitch-thumb": {
            color: theme.palette.grey[100],
            ...theme.applyStyles("dark", {
              color: theme.palette.grey[600],
            }),
          },

          "&.Mui-disabled + .MuiSwitch-track": {
            opacity: 0.7,
            ...theme.applyStyles("dark", {
              opacity: 0.3,
            }),
          },
        }),

        thumb: {
          boxSizing: "border-box",
          width: 22,
          height: 22,
        },

        track: ({ theme }) => ({
          borderRadius: 26 / 2,
          backgroundColor: "#E9E9EA",
          opacity: 1,
          transition: theme.transitions.create(["background-color"], {
            duration: 500,
          }),
          ...theme.applyStyles("dark", {
            backgroundColor: "#39393D",
          }),
          [theme.breakpoints.only("xs")]: {
            backgroundColor: lighten(theme.palette.primary.main, 0.85),
          },
        }),
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          height: 26,
          padding: "0 5px",
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontSize: "22px",
          fontWeight: 600,
          padding: "24px",
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        message: ({ theme }) => ({
          fontSize: theme.typography.body1.fontSize,
        }),
        standardWarning: ({ theme }) => ({
          backgroundColor: theme.palette.warning.main,
          color: theme.palette.warning.contrastText,
          border: `1px solid ${theme.palette.divider}`,
          "& .MuiAlert-icon": {
            color: theme.palette.warning.contrastText,
          },
        }),
        standardError: ({ theme }) => ({
          backgroundColor: theme.palette.error.light,
          color: theme.palette.error.main,
          border: `1px solid ${theme.palette.divider}`,
          "& .MuiAlert-icon": {
            color: theme.palette.error.main,
          },
        }),
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: Number(theme.shape.borderRadius) * 4,
        }),
      },
    },
    MuiCssBaseline: {
      styleOverrides: (theme: Theme) => ({
        ".notistack-SnackbarContainer": {
          top: 110,
          right: 60,
          [theme.breakpoints.only("xs")]: {
            top: 150,
            right: 0,
            left: 0,
            width: "100%",
            maxWidth: "100%",
            padding: "0 16px",
          },
        },
        ".notistack-MuiContent": {
          borderRadius: 20,
          padding: "8px 16px",
          boxShadow: "0px 4px 10px 0px rgba(0, 0, 0, 0.2)",
        },
        ".notistack-MuiContent-error": {
          backgroundColor: theme.palette.error.light,
          color: theme.palette.error.main,
        },

        ".notistack-MuiContent-success": {
          backgroundColor: theme.palette.success.light,
          color: theme.palette.success.main,
        },

        ".notistack-MuiContent-warning": {
          backgroundColor: theme.palette.warning.light,
          color: theme.palette.warning.main,
        },

        ".notistack-MuiContent-info": {
          backgroundColor: theme.palette.info.light,
          color: theme.palette.info.main,
        },
      }),
    },
  },
};

function convertOverridesToThemeOptions(overrides: ThemeOverrides): ThemeOptions {
  const options: ThemeOptions = {};

  if (overrides.primaryColor || overrides.accentColor || overrides.backgroundColor) {
    options.palette = {};

    if (overrides.primaryColor) {
      options.palette.primary = {
        main: overrides.primaryColor,
        ...(overrides.primaryContrastText && { contrastText: overrides.primaryContrastText }),
      };
    }

    if (overrides.accentColor) {
      options.palette.text = { accent: overrides.accentColor };
    }

    if (overrides.backgroundColor) {
      options.palette.background = { default: overrides.backgroundColor };
    }
  }

  if (overrides.borderRadius) {
    options.shape = {
      borderRadius: parseInt(overrides.borderRadius, 10),
    };
  }

  return options;
}

export function createWidgetTheme(themeName: ThemeName = "campnerd", overrides?: ThemeOverrides) {
  const presetConfig = THEME_PRESETS[themeName];
  let themeConfig = deepmerge(baseThemeConfig, presetConfig);

  if (overrides) {
    const customOptions = convertOverridesToThemeOptions(overrides);
    themeConfig = deepmerge(themeConfig, customOptions);
  }

  return createTheme(themeConfig);
}

export const theme = createWidgetTheme();

export const primaryActionSx = (theme: Theme) => {
  const baseRgb = hexToRgb(theme.palette.primary.main);

  const startColor = adjustColor(baseRgb, {
    sat: 1.35,
    light: -0.07,
  });
  const endColor = adjustColor(baseRgb, {
    sat: 2,
    light: +0.09,
  });
  return {
    "&:not(.Mui-disabled)": {
      background: `linear-gradient(90deg, ${startColor} 0%, ${endColor} 100%)`,
    },

    "&:not(.Mui-disabled):hover": {
      background: `linear-gradient(90deg, ${startColor} 0%, ${endColor} 100%)`,
      filter: "brightness(1.08)",
    },
  };
};
